import type { CompendiumEntry, CompendiumKind } from "./compendium";

export type LinkKind = CompendiumKind | "character";

export type LinkableEntry = Pick<CompendiumEntry, "id" | "name" | "aliases" | "looseCase"> & {
  kind: LinkKind;
};

export interface TextPart {
  text: string;
  /** Set when this part of the text names a compendium entry. */
  entry?: { id: string; kind: LinkKind };
}

const WORD_END = /[\p{L}\p{N}]$/u;
const straight = (text: string): string => text.replace(/’/g, "'");
const asPattern = (name: string): string => name
  .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  .replace(/'/g, "['’]");

/** Every spelling of an entry. Ordinary words also take a plural. */
function spellings(entry: LinkableEntry): string[] {
  const names = [entry.name, ...entry.aliases].map(straight);
  return entry.looseCase
    ? names.flatMap((name) => (name.endsWith("s") ? [name] : [name, `${name}s`]))
    : names;
}

const sameEntry = (a: LinkableEntry, skip: Pick<LinkableEntry, "id" | "kind"> | undefined): boolean => (
  skip !== undefined && a.id === skip.id && a.kind === skip.kind
);

/**
 * Splits text so every mention of an entry can link to its page. A page's own entry
 * still matches, so its name stays whole, but comes back as plain text.
 */
export function splitMentions(
  text: string,
  entries: readonly LinkableEntry[],
  skip?: Pick<LinkableEntry, "id" | "kind">,
): TextPart[] {
  const exact = new Map<string, LinkableEntry>();
  const loose = new Map<string, LinkableEntry>();
  for (const entry of entries) {
    for (const name of spellings(entry)) {
      if (!exact.has(name)) {
        exact.set(name, entry);
      }
      if (entry.looseCase && !loose.has(name.toLowerCase())) {
        loose.set(name.toLowerCase(), entry);
      }
    }
  }
  if (exact.size === 0) {
    return [{ text }];
  }
  // Longest first, so "Yeerks" is one mention and not "Yeerk" and a stray "s".
  const names = [...exact.keys()].sort((a, b) => b.length - a.length).map(asPattern);
  // No lookbehind: iPhones before iOS 16.4 cannot even parse it, which would crash the captions.
  const pattern = new RegExp(`(${names.join("|")})(?![\\p{L}\\p{N}])`, "giu");
  const startsMidWord = (index: number): boolean => (
    index > 0 && WORD_END.test(text.slice(0, index))
  );
  const parts: TextPart[] = [];
  let at = 0;
  for (const match of text.matchAll(pattern)) {
    if (startsMidWord(match.index)) {
      continue;
    }
    const said = straight(match[0]);
    const entry = exact.get(said) ?? loose.get(said.toLowerCase());
    if (entry === undefined || sameEntry(entry, skip)) {
      continue;
    }
    if (match.index > at) {
      parts.push({ text: text.slice(at, match.index) });
    }
    parts.push({ text: match[0], entry: { id: entry.id, kind: entry.kind } });
    at = match.index + match[0].length;
  }
  if (at < text.length) {
    parts.push({ text: text.slice(at) });
  }
  return parts;
}
